<template>
  <div class="w-full h-full pt-5">
    <div class="flex items-center">
      <h1 class="text-xl font-extrabold pl-4 mr-8">Overview</h1>
      <Breadcrumb :items="breadcrumbs" />
    </div>

    <div v-if="loading" class="h-full flex items-center justify-center">
      <loader size="xxs" :loader-image="false" />
    </div>

    <template v-else>
      <div v-if="!showError" class="mx-4 mt-3">
        <div class="w-full flex flex-col md:flex-row gap-4 mt-2">
          <Card class="text-white p-4 flex-1" style="background: #3454d1">
            <div class="flex items-center">
              <Icon class-name="text-white" icon-name="union" size="l" />
              <div class="flex flex-col ml-4">
                <span class="font-thin">Next Pay Cycle</span>
                <span class="text-lg font-bold">
                  {{ payrunData.nextPaydate ? setDate(payrunData.nextPaydate) : 'N/A' }}
                </span>
              </div>
              <div class="border bg-white w-px mx-4 h-12" />
              <div class="flex flex-col">
                <span class="font-thin">Amount Due</span>
                <span class="text-lg font-bold">
                  {{ convertToCurrency(payrunData.amountDue) }}
                </span>
              </div>
            </div>
          </Card>

          <Card class="text-white p-4 flex-1 relative" style="background: #321c3b">
            <div @mouseenter="isPayrunToolTip = true" @mouseleave="isPayrunToolTip = false"
              class="absolute top-2 right-2">
              <Icon icon-name="information-line" class="cursor-pointer" size="xsm" />
              <ToolTip v-if="isPayrunToolTip" style="width: 250px" left="-119px">
                <p class="w-full mx-auto text-justify text-xs font-normal">
                  Backpay to access and process salary arrears.
                </p>
                <p class="w-full cursor-pointer text-xs text-blueCrayola flex items-center gap-2">
                  <span>Go to Off-Cycle Pay</span>
                  <Icon icon-name="right-arrow" class="text-blueCrayola" size="s" />
                </p>
              </ToolTip>
            </div>

            <div class="flex items-center">
              <Icon icon-name="wallet" size="l" />
              <div class="flex flex-col ml-4">
                <span class="font-semibold text-xs uppercase">PAY RUNS (YTD)</span>
                <span class="font-bold text-lg">{{ payrunData.payrunCount }}</span>
              </div>
              <div class="border bg-romanSilver w-px ml-4 h-12" />
              <div class="flex flex-col ml-4">
                <span class="font-semibold text-xs uppercase">TOTAL PAYROLL COST (YTD)</span>
                <span class="font-bold text-lg">
                  {{ convertToCurrency(payrunData.payrollCost) }}
                </span>
              </div>
            </div>
          </Card>

          <Card class="text-white p-4 flex-1" style="background: #13b56a">
            <div class="flex items-center">
              <Icon icon-name="employee_payrun" class-name="ml-2" size="m" />
              <div class="flex flex-col ml-6">
                <span class="font-semibold text-xs uppercase">Current no.of Employees</span>
                <span class="font-bold text-lg pt-1">
                  {{ payrunData.activePayrollEmployees }}
                </span>
              </div>
            </div>
          </Card>
        </div>

        <div v-if="payrunData.payrunCount !== 0" class="w-full mt-6">
          <Card class="w-full p-4 mb-4">
            <div class="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
              <h1 class="font-bold text-lg mb-2 md:mb-0">Pay Run Summary</h1>
              <p class="text-base md:w-2/3">
                This is an overview of your pay run summary throughout the months of the year.
              </p>
              <div class="mt-2 md:mt-0">
                <c-select placeholder="--Select--" :options="overviewYears" @onChange="handleYearsChange" v-model="year"
                  label="Year to Date" variant="w-full md:w-40" />
              </div>
            </div>

            <sTable v-if="payrunData.payrunCount > 0" :headers="PayHeader" :items="payrunTableData" class="w-full mt-4"
              :has-number="false" :pagination-list="metaData" @page="handlePage($event)" page-sync
              @itemsPerPage="handleItemsPerPage($event)" :loading="loadingTable">
              <template v-slot:item="{ item }">
                <template v-if="item.year">
                  {{ `${$DATEFORMAT(new Date(item.data.year, item.data.month - 1, 1), "LLLL")} ${item.data.year}` }}
                </template>
                <template v-else-if="item.netEarnings">
                  {{ convertToCurrency(item.data.netEarnings) }}
                </template>
                <template v-else-if="item.payFrequency">
                  {{ handlePayFrequency(item.data.payFrequency) }}
                </template>
                <template v-else-if="item.paySplitPosition">
                  {{ `${$getOrdinal(item.data.paySplitPosition)} Payment` }}
                </template>
                <template v-else-if="item.payDate">
                  {{ item.data.payDate ? $DATEFORMAT(new Date(item.data.payDate), "MMMM dd, yyyy") : "-" }}
                </template>
                <template v-else-if="item.payrollStatus">
                  <span :class="getStatusClass(item.data.payrollStatus)"
                    class="flex p-2 capitalize items-center justify-center rounded text-white">
                    {{ item.data.payrollStatus }}
                  </span>
                </template>
                <template v-else-if="item.payType" class="capitalize">
                  {{ item.data.payType }}
                </template>
              </template>
            </sTable>
          </Card>

          <Card class="w-full p-4 flex flex-col md:flex-row gap-4">
            <div class="w-full md:w-1/2">
              <div class="flex justify-between items-center mb-4">
                <p class="font-bold text-lg">Pay Run Categories</p>
                <Button class="text-white" :background="appearance.buttonColor || defaultBTNColor"
                  @click="handlePayrunView">
                  Go to Pay Run
                </Button>
              </div>

              <div class="flex flex-col items-center">
                <div class="relative">
                  <DonutChart v-if="Object.keys(categoryObject).length > 0" :data="categoryObject"
                    :options="chartOptions" :styles="myStyles" :value="categoryTotal" />
                </div>

                <div class="w-full grid grid-cols-1 sm:grid-cols-3 gap-2 mt-4">
                  <div v-for="(legend, index) in categoryLegend" :key="index" class="flex items-center">
                    <div class="rounded-full mr-2 w-3 h-3" :style="`background-color: ${legend.color}`" />
                    <div class="text-sm text-gray-600">
                      {{ legend.name }}: {{ legend.value }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="border border-gray-200 my-4 md:my-0 md:mx-4 md:h-auto md:w-px" />

            <div class="w-full md:w-1/2">
              <div class="flex justify-between items-center mb-4">
                <p class="font-bold text-lg">Pay Run Status</p>
                <Button class="text-white" :background="appearance.buttonColor || defaultBTNColor"
                  @click="handlePayrunView">
                  Go to Pay Run
                </Button>
              </div>

              <div class="flex flex-col items-center">
                <div class="relative">
                  <DonutChart v-if="Object.keys(chartDataObject).length > 0" :data="chartDataObject"
                    :options="chartOptions" :styles="myStyles" :value="payrunTotal" />
                </div>

                <div class="w-full grid grid-cols-1 sm:grid-cols-3 gap-2 mt-4">
                  <div v-for="(legend, index) in chartLegend" :key="index" class="flex items-center">
                    <div class="rounded-full mr-2 w-3 h-3" :style="`background-color: ${legend.color}`" />
                    <div class="text-sm text-gray-600">
                      {{ legend.name }}: {{ legend.value }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>

        <div v-else class="mt-12">
          <Card>
            <p class="text-gray-500 py-12 text-center">
              Pay runs will be listed here once your organization runs a pay run.
            </p>
          </Card>
        </div>
      </div>

      <div v-else>
        <error-page />
      </div>
    </template>
  </div>
</template>

<script>
import { mapState } from "vuex";
import Breadcrumb from "@scelloo/cloudenly-ui/src/components/bread-crumb";
import STable from "@scelloo/cloudenly-ui/src/components/table";
import Card from "@/components/Card";
import Button from "@/components/Button";
import ToolTip from "@/components/ToolTip";
import ErrorPage from "@/modules/Admin/error403";
import DonutChart from "@/components/donutChart";

export default {
  name: "PayrollOverview",
  components: {
    Card,
    STable,
    DonutChart,
    Breadcrumb,
    ErrorPage,
    Button,
    ToolTip,
    CSelect: () => import("@scelloo/cloudenly-ui/src/components/select"),
  },
  data() {
    return {
      loading: true,
      showError: false,
      loadingTable: false,
      isPayrunToolTip: false,
      year: new Date().getFullYear(),
      payrunData: {},
      payrunTableData: [],
      metaData: {},
      overviewYears: [],
      categoryObject: {},
      categoryLegend: [],
      categoryTotal: 0,
      chartDataObject: {},
      chartLegend: [],
      payrunTotal: 0,
      PayHeader: [
        { title: "Pay cycle", value: "year" },
        { title: "Pay run Type", value: "payType" },
        { title: "Pay Frequency", value: "payFrequency" },
        { title: "Pay SCHEDULE", value: "paySplitPosition" },
        { title: "Amount Processed", value: "netEarnings" },
        { title: "Pay Date", value: "payDate" },
        { title: "Status", value: "payrollStatus" },
      ],
      breadcrumbs: [
        {
          disabled: false,
          text: "Payroll",
          href: "Performance",
          id: "Performance",
        },
        {
          disabled: false,
          text: "Overview",
          href: "Performance Agreements",
          id: "Agreement",
        },
      ],
      chartOptions: {
        legend: {
          display: false,
          position: "bottom"
        },
        responsive: true,
        maintainAspectRatio: false
      },
      myStyles: {
        height: `300px`,
        width: `300px`,
        position: "relative"
      },
    };
  },
  computed: {
    ...mapState({
      defaultBTNColor: (state) => state.orgAppearance.defaultBtnColor,
      appearance: (state) => state.orgAppearance.orgAppearanceData,
    }),
  },
  methods: {
    async viewDashboard() {
      try {
        await this.$handlePrivilege("overviewHR", "viewOverview");
        this.showError = false;
        this.getOverviewYear();
        this.queryUpdate();
        this.fillPreviousPayrunChartData();
      } catch (error) {
        this.showError = true;
      } finally {
        this.loading = false;
      }
    },
    handleItemsPerPage(itemNo) {
      this.itemsPerPage = itemNo;
      this.queryUpdate();
    },
    handlePage(pageNumber) {
      this.numberOfPage = pageNumber;
      this.queryUpdate();
    },
    queryUpdate() {
      this.loadingTable = true;
      const pageNumber = this.numberOfPage ? `&page=${this.numberOfPage}` : '';
      const itemPage = this.itemsPerPage ? `&perPage=${this.itemsPerPage}` : '';
      this.getAllAnalytics(this.year, pageNumber, itemPage);
    },
    setDate(date) {
      if (!date) return 'N/A';
      const newDate = new Date(date);
      newDate.setHours(newDate.getHours() + 8);
      return this.$DATEFORMAT(newDate, "do, LLLL yyyy");
    },
    handlePayFrequency(value) {
      const frequencies = {
        twice_monthly: "Twice Monthly",
        monthly: "Monthly",
        daily: "Daily",
        weekly: "Four Times Monthly",
      };
      return frequencies[value] || value;
    },
    async handlePayrunView() {
      try {
        await this.$handlePrivilege("overviewHR", "goToPayRun");
        this.$router.push({ name: 'Pay Runs' });
      } catch (error) {
        this.$toasted.error("You do not have permission to perform this task", { duration: 5000 });
      }
    },
    getStatusClass(status) {
      const classes = {
        paid: "account_active",
        review: "account_pending",
        approved: "account_pending",
        draft: "bg-gray-500",
        disapproved: "account_failed",
      };
      return classes[status] || "account_failed";
    },
    convertToCurrency(value) {
      return value ? `₦${parseFloat(value).toLocaleString()}` : '₦0';
    },
    async getAllAnalytics(year, pageNumber, itemPage) {
      try {
        const response = await this.$_getPayrollDashboard(`year=${year}${itemPage}${pageNumber}`);
        this.payrunData = response.data;
        this.payrunTableData = response.data.payruns || [];
        this.metaData = response.data.meta || {};

        this.processCategoryData(response.data.categories || {});

        this.loadingTable = false;
      } catch (error) {
        this.loadingTable = false;
      }
    },
    processCategoryData(categories) {
      this.categoryLegend = [
        { color: "#35BA83", name: "Regular Pay", value: categories.regular || 0 },
        { color: "#114FF5", name: "Off-Cycle Pay", value: categories.offCyle || 0 },
        { color: "#878E99", name: "Terminal Pay", value: categories.terminal || 0 },
      ];

      this.categoryTotal = (categories.regular || 0) + (categories.offCyle || 0) + (categories.terminal || 0);

      this.categoryObject = {
        labels: ["Regular", "Off-Cycle", "Terminal"],
        datasets: [{
          backgroundColor: ["#35BA83", "#114FF5", "#878E99"],
          data: [
            categories.regular || 0,
            categories.offCyle || 0,
            categories.terminal || 0
          ],
        }]
      };
    },
    async fillPreviousPayrunChartData() {
      try {
        const response = await this.$_getPayrollItems();
        const data = response.data || {};

        this.chartLegend = [
          { color: "#878E99", name: "Drafts", value: data.draft || 0 },
          { color: "#E99323", name: "In-review", value: data.review || 0 },
          { color: "#13B56A", name: "Paid", value: data.paid || 0 },
          { color: "#321C3B", name: "Approved", value: data.approved || 0 },
          { color: "#EA3C53", name: "Disapproved", value: data.disapproved || 0 },
        ];

        this.payrunTotal = Object.values(data).reduce((sum, val) => sum + (parseInt(val, 10) || 0), 0);

        this.chartDataObject = {
          labels: ["Drafts", "In-review", "Paid", "Approved", "Disapproved"],
          datasets: [{
            backgroundColor: ["#878E99", "#E99323", "#13B56A", "#321C3B", "#EA3C53"],
            data: [
              data.draft || 0,
              data.review || 0,
              data.paid || 0,
              data.approved || 0,
              data.disapproved || 0,
            ],
            datalabels: {
              display: false
            }
          }]
        };
      } catch (error) {
        throw new Error(error)
      }
    },
    async getOverviewYear() {
      try {
        const response = await this.$_getOverviewYear();
        this.overviewYears = response.data.years || [];
      } catch (error) {
        throw new Error(error)
      }
    },
    handleYearsChange(value) {
      this.year = value;
      this.loading = true;
      this.queryUpdate();
    },
  },
  mounted() {
    this.viewDashboard();
  },
};
</script>

<style scoped>
.account_pending {
  @apply bg-yellow-100 text-yellow-800;
}

.account_active {
  @apply bg-green-100 text-green-800;
}

.account_failed {
  @apply bg-red-100 text-red-800;
}
</style>